import { Routes } from '@angular/router';
import { ROUTES } from './core/routes';
import { protectedGuard } from './core/guards/protected.guard';
import { guestGuard } from './core/guards/guest.guard';
import { onboardingGuard } from './core/guards/onboarding.guard';
import { rootRedirectGuard } from './core/guards/root-redirect.guard';
import { appConfigReadyGuard } from './core/guards/app-config-ready.guard';

/**
 * Angular route table — mirrors Consumer's app/routes.tsx `<Routes>` tree
 * 1:1 (same paths, same guard-per-route mapping). `loadComponent` gives the
 * same route-level code-splitting as Consumer's `lazy(() => import(...))`.
 *
 * Every leaf route below also carries `appConfigReadyGuard` (in addition to
 * its auth guard, where present) since every one of these screens renders
 * `<app-cms-background>` — see that guard's doc comment for the
 * fallback-image-flash bug this closes.
 */
export const routes: Routes = [
  {
    // Splash (subscriber-ID entry) is retired — /login is the single login entry now.
    path: ROUTES.SPLASH,
    redirectTo: ROUTES.LOGIN,
    pathMatch: 'full',
  },
  {
    // Primary login flow at /login — phone number → account lookup → set-password/OTP
    // (registration) or password (login). Replaces Splash + the old Login component.
    path: ROUTES.LOGIN,
    canActivate: [guestGuard, appConfigReadyGuard],
    loadComponent: () =>
      import('./modules/auth/view/login-phonenumber/login-phonenumber.component').then((m) => m.LoginPhoneNumberComponent),
  },
  {
    path: ROUTES.ONBOARDING_INTRO,
    canActivate: [onboardingGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/onboarding/view/intro/intro.component').then((m) => m.IntroComponent),
  },
  {
    path: ROUTES.ONBOARDING,
    canActivate: [onboardingGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/onboarding/view/free-trial/free-trial.component').then((m) => m.FreeTrialComponent),
  },
  {
    path: ROUTES.ONBOARDING_PLANS,
    canActivate: [onboardingGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/onboarding/view/trial-plans/trial-plans.component').then((m) => m.TrialPlansComponent),
  },
  {
    path: ROUTES.ONBOARDING_PAYMENT,
    canActivate: [onboardingGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/onboarding/view/trial-payment-qr/trial-payment-qr.component').then((m) => m.TrialPaymentQrComponent),
  },
  {
    path: ROUTES.ONBOARDING_PAYMENT_RESULT,
    canActivate: [onboardingGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/onboarding/view/trial-payment-result/trial-payment-result.component').then((m) => m.TrialPaymentResultComponent),
  },
  {
    path: ROUTES.ONBOARDING_VOUCHER,
    canActivate: [onboardingGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/onboarding/view/voucher-redemption/voucher-redemption.component').then((m) => m.VoucherRedemptionComponent),
  },
  {
    path: ROUTES.ONBOARDING_VOUCHER_CONFIRMATION,
    canActivate: [onboardingGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/onboarding/view/voucher-confirmation/voucher-confirmation.component').then((m) => m.VoucherConfirmationComponent),
  },
  {
    path: ROUTES.NOT_ELIGIBLE,
    canActivate: [guestGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/onboarding/view/not-eligible/not-eligible.component').then((m) => m.NotEligibleComponent),
  },
  {
    path: ROUTES.REGISTRATION,
    canActivate: [guestGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/registration/view/registration/registration.component').then((m) => m.RegistrationComponent),
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    canActivate: [guestGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/forgotpassword/view/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },
  {
    path: ROUTES.DASHBOARD,
    canActivate: [protectedGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/dashboard/view/dashboard/dashboard.component').then((m) => m.DashboardComponent),
  },
  {
    // Profile's background is a plain literal `bg-[#141414]`, not
    // `<app-cms-background>` (see angular-poc-parity-report.md), so it
    // doesn't need to wait on CMS config here.
    path: ROUTES.PROFILE,
    canActivate: [protectedGuard],
    loadComponent: () => import('./modules/profile/view/profile/profile.component').then((m) => m.ProfileComponent),
  },
  {
    path: ROUTES.FEEDBACK,
    canActivate: [protectedGuard, appConfigReadyGuard],
    loadComponent: () => import('./modules/feedback/view/feedback/feedback.component').then((m) => m.FeedbackComponent),
  },
  // Mirrors Consumer's `RootRedirect`: decide Dashboard vs Splash based on
  // auth state (after waiting for bootstrap hydration) instead of always
  // landing on Splash — see root-redirect.guard.ts's doc comment.
  {
    path: '',
    pathMatch: 'full',
    canActivate: [rootRedirectGuard],
    loadComponent: () =>
      import('./core/components/root-redirect-placeholder.component').then((m) => m.RootRedirectPlaceholderComponent),
  },
  {
    path: '**',
    canActivate: [rootRedirectGuard],
    loadComponent: () =>
      import('./core/components/root-redirect-placeholder.component').then((m) => m.RootRedirectPlaceholderComponent),
  },
];
